import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getActivityLogs, getCardById, ActivityLog, Card } from '../api';
import Button from '../components/common/Button';
import ResponsiveHeader from "../components/common/ResponsiveHeader";

// --- 스타일 정의 (반응형 적용) ---
const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: var(--bg-main);
  color: var(--text-bright);
  overflow: hidden;
`;

const DateInput = styled.input`
  background-color: rgba(33, 37, 43, 0.8);
  color: var(--text-bright);
  border: 1px solid var(--border-color);
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  cursor: pointer;
  backdrop-filter: blur(4px);
  &::-webkit-calendar-picker-indicator { filter: invert(1); cursor: pointer; }
`;

const CanvasContainer = styled.div`
  flex: 1;
  position: relative;
  background-color: #1d2125;
  overflow: hidden;
  background-image:
          linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
  cursor: grab;
  &:active { cursor: grabbing; }

  /* 모바일 터치 동작 최적화 */
  touch-action: none;
`;

const TransformLayer = styled.div`
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  transform-origin: 0 0;
  will-change: transform;
`;

const CanvasSVG = styled.svg`
  width: 100%; height: 100%;
  position: absolute;
  top: 0; left: 0;
  overflow: visible;
  pointer-events: none;
`;

const ZoomControls = styled.div`
  position: absolute;
  bottom: 180px;
  right: 30px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: 15;

  @media (max-width: 768px) {
    bottom: 240px;
    right: 15px;
  }
`;

const ZoomButton = styled.button`
  width: 36px; height: 36px;
  background-color: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  color: var(--text-bright);
  font-size: 1.2rem;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
  &:hover { background-color: var(--bg-input); color: var(--primary-accent); }
`;

const isMobile = window.innerWidth < 768;
const CARD_WIDTH = isMobile ? 160 : 220;
const CARD_HEIGHT = isMobile ? 80 : 100;

const CardNodeContainer = styled.div<{ x: number, y: number, isActive: boolean }>`
  position: absolute;
  left: 0; top: 0;
  transform: translate(${props => props.x}px, ${props => props.y}px) translate(-50%, -50%);
  width: ${CARD_WIDTH}px;
  min-height: ${CARD_HEIGHT}px;
  background-color: var(--bg-card);
  border-radius: 6px;
  padding: 0.75rem;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, opacity 0.2s;
  z-index: ${props => props.isActive ? 10 : 1};
  border: 2px solid ${props => props.isActive ? '#FFD700' : 'var(--border-color)'};
  box-shadow: ${props => props.isActive ? '0 0 20px rgba(255, 215, 0, 0.4)' : '0 4px 6px rgba(0,0,0,0.1)'};
  opacity: ${props => props.isActive ? 1 : 0.6};
  display: flex; flex-direction: column; justify-content: center;
  &:hover { z-index: 11; opacity: 1; border-color: var(--primary-accent); }

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;
const CardTitle = styled.div`
  font-weight: bold; color: var(--text-bright); font-size: 0.9rem; margin-bottom: 0.5rem; word-break: break-word; line-height: 1.3;
  @media (max-width: 768px) {
    font-size: 0.75rem;
    margin-bottom: 0.2rem;
  }
`;
const CardMeta = styled.div`
  font-size: 0.75rem; color: var(--text-dark); display: flex; justify-content: space-between; margin-top: auto;
  @media (max-width: 768px) {
    font-size: 0.65rem;
  }
`;
const LabelContainer = styled.div`display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 0.5rem;`;
const LabelPill = styled.span<{ color: string }>`
  background-color: ${props => props.color}; color: white; padding: 2px 6px; border-radius: 4px; font-size: 0.7rem; font-weight: 600;
  @media (max-width: 768px) {
    font-size: 0.6rem;
    padding: 1px 4px;
  }
`;

const ControlBar = styled.div`
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 160px;
  background: linear-gradient(0deg, #16191d 0%, rgba(22, 25, 29, 0.95) 100%);
  display: flex; flex-direction: column; justify-content: center; padding: 0 2rem;
  z-index: 20; border-top: 1px solid var(--border-color);

  @media (max-width: 768px) {
    height: 220px;
    padding: 0 1rem;
  }
`;

const TimeDisplay = styled.div`
  font-family: monospace; font-size: 1.8rem; font-weight: bold; color: var(--primary-accent);
  text-align: center; margin-bottom: 0.2rem; letter-spacing: 2px;
  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const TimelineScrollContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  overflow-x: auto;
  padding-bottom: 10px;
  &::-webkit-scrollbar { height: 8px; }
  &::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.05); border-radius: 4px; }
  &::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.2); border-radius: 4px; }
  &::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.3); }
`;

const SliderWrapper = styled.div<{ widthPercent: number }>`
  width: ${props => props.widthPercent}%;
  min-width: 100%;
  position: relative;
  transition: width 0.3s ease-out;
  padding: 0 10px;
`;

const RangeInput = styled.input`
  width: 100%; -webkit-appearance: none; background: transparent; height: 6px; cursor: pointer; border-radius: 3px; position: relative; z-index: 2;
  &::-webkit-slider-runnable-track { width: 100%; height: 6px; background: rgba(255, 255, 255, 0.1); border-radius: 3px; }
  &::-webkit-slider-thumb { -webkit-appearance: none; height: 20px; width: 20px; background: var(--primary-accent); border: 2px solid white; border-radius: 50%; margin-top: -7px; box-shadow: 0 0 10px rgba(0,0,0,0.5); transition: transform 0.1s; }
  &:active::-webkit-slider-thumb { transform: scale(1.2); }
`;

const LogMarkerContainer = styled.div`
  position: absolute;
  top: 0; bottom: 0; left: 0; width: 100%;
  pointer-events: none;
  z-index: 5;
`;

const LogMarker = styled.div`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 3px;
  height: 10px;
  background-color: rgba(255, 215, 0, 0.7);
  border-radius: 2px;
  pointer-events: auto;
  cursor: pointer;
  transition: all 0.15s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  &:hover {
    height: 24px;
    width: 6px;
    background-color: #FFD700;
    box-shadow: 0 0 8px rgba(255, 215, 0, 0.8);
    z-index: 10;
  }
`;

const TickContainer = styled.div`display: flex; justify-content: space-between; margin-top: 10px; padding: 0 10px; user-select: none; pointer-events: none;`;
const TickMark = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  &::before { content: ''; width: 2px; height: 6px; background-color: var(--text-dark); }
`;
const TickLabel = styled.span`font-size: 0.7rem; color: var(--text-dark); white-space: nowrap;`;

const ControlsContainer = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 1.5rem; margin-top: 0.5rem;
  flex-wrap: wrap;
`;
const PlayButton = styled.button`
  background: var(--bg-list); border: 1px solid var(--border-color); color: var(--text-bright); width: 40px; height: 40px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s;
  &:hover { background: var(--primary-accent); border-color: var(--primary-accent); color: white; }
  svg { width: 16px; height: 16px; fill: currentColor; }
`;
const SpeedSelect = styled.select`
  background-color: var(--bg-input); color: var(--text-bright); border: 1px solid var(--border-color); padding: 0.3rem 0.5rem; border-radius: 4px; font-size: 0.9rem; cursor: pointer; outline: none;
`;
const TimelineZoomButton = styled.button<{ isActive?: boolean }>`
  background: ${props => props.isActive ? 'var(--primary-accent)' : 'var(--bg-input)'};
  color: ${props => props.isActive ? 'white' : 'var(--text-dark)'};
  border: 1px solid var(--border-color);
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  &:hover { background-color: var(--primary-accent); color: white; }
`;

const SEC_PER_DAY = 24 * 60 * 60;
const SPEED_OPTIONS = [1, 2, 5, 10, 20, 60, 600];

const getIntersectionPoint = (source: {x: number, y: number}, target: {x: number, y: number}, rectWidth: number, rectHeight: number) => {
    const dx = target.x - source.x;
    const dy = target.y - source.y;
    if (Math.abs(dx) < 0.001) return { x: target.x, y: target.y - (Math.sign(dy) * rectHeight / 2) };
    const slope = dy / dx;
    const hHalf = rectHeight / 2;
    const wHalf = rectWidth / 2;
    const dirX = -Math.sign(dx);
    const yOnVertical = slope * (dirX * wHalf);
    if (Math.abs(yOnVertical) <= hHalf) return { x: target.x + (dirX * wHalf), y: target.y + yOnVertical };
    const dirY = -Math.sign(dy);
    const xOnHorizontal = (dirY * hHalf) / slope;
    return { x: target.x + xOnHorizontal, y: target.y + (dirY * hHalf) };
};

const ActivityPlaybackPage = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [logs, setLogs] = useState<ActivityLog[]>([]);
    const [cardMap, setCardMap] = useState<Record<string, Card>>({});

    const initialTimeParam = searchParams.get('time');
    const [currentTime, setCurrentTime] = useState(initialTimeParam ? Number(initialTimeParam) : 32400);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const [timelineZoom, setTimelineZoom] = useState(1);

    const requestRef = useRef<number>(0);
    const lastTimeRef = useRef<number>(0);
    const isFirstRender = useRef(true);
    const currentTimeRef = useRef(currentTime);

    const [viewState, setViewState] = useState({ scale: 1, x: 0, y: 0 });
    const isDragging = useRef(false);
    const lastMousePos = useRef({ x: 0, y: 0 });

    useEffect(() => { currentTimeRef.current = currentTime; }, [currentTime]);

    // ✨ [수정] useCallback 및 functional update 사용으로 의존성 제거
    const updateUrlTime = useCallback((time: number) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('time', Math.floor(time).toString());
            return newParams;
        }, { replace: true });
    }, [setSearchParams]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            switch(e.code) {
                case 'Space': e.preventDefault(); setIsPlaying(prev => !prev); break;
                case 'ArrowLeft':
                    e.preventDefault();
                { const newTime = Math.max(0, currentTimeRef.current - 60); setCurrentTime(newTime); updateUrlTime(newTime); }
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                { const newTime = Math.min(SEC_PER_DAY, currentTimeRef.current + 60); setCurrentTime(newTime); updateUrlTime(newTime); }
                    break;
                case 'ArrowUp': e.preventDefault(); setPlaybackSpeed(prev => { const idx = SPEED_OPTIONS.indexOf(prev); return SPEED_OPTIONS[Math.min(SPEED_OPTIONS.length - 1, idx + 1)]; }); break;
                case 'ArrowDown': e.preventDefault(); setPlaybackSpeed(prev => { const idx = SPEED_OPTIONS.indexOf(prev); return SPEED_OPTIONS[Math.max(0, idx - 1)]; }); break;
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [updateUrlTime]); // updateUrlTime이 안전해졌으므로 추가 가능

    // ✨ [수정] currentTimeRef와 updateUrlTime을 사용하여 경고 해결 및 무한 루프 방지
    useEffect(() => {
        if (isFirstRender.current) { isFirstRender.current = false; return; }
        if (!isPlaying) { updateUrlTime(currentTimeRef.current); }
    }, [isPlaying, updateUrlTime]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await getActivityLogs(selectedDate);
                const sortedLogs = res.data.sort((a, b) =>
                    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
                );

                // ✨ 필터링 로직 수정: 코멘트 로그도 포함 (targetType 필터 제거 혹은 조정)
                const filteredLogs = sortedLogs.filter(log => {
                    // 유효한 변경이 있는 UPDATE거나, CREATE/DELETE인 경우 (타입 불문)
                    return log.type !== 'UPDATE' || (log.diff && Object.keys(log.diff).length > 0);
                });

                setLogs(filteredLogs);

                // initialTimeParam을 의존성에 넣으면 재생 시 URL 변경 -> 리렌더 -> 재요청 루프 발생하므로 제외
                if (!initialTimeParam && filteredLogs.length > 0) {
                    const firstLogDate = new Date(filteredLogs[0].timestamp);
                    const startSeconds = firstLogDate.getHours() * 3600 + firstLogDate.getMinutes() * 60 + firstLogDate.getSeconds();
                    setCurrentTime(startSeconds);
                }

                const uniqueIds = Array.from(new Set(filteredLogs.map(l => l.targetId).filter(id => id))) as string[];
                const cardDataMap: Record<string, Card> = {};
                await Promise.all(uniqueIds.map(async (id) => {
                    try {
                        const cardRes = await getCardById(id);
                        cardDataMap[id] = cardRes.data;
                    } catch {
                        cardDataMap[id] = { id, title: '삭제된 카드', labels: [] } as any;
                    }
                }));
                setCardMap(cardDataMap);
            } catch (e) { console.error(e); }
        };
        fetchData();
        // ✨ [수정] initialTimeParam으로 인한 불필요한 재요청 방지를 위해 경고 무시
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate]);

    const layout = useMemo(() => {
        const validIds = Object.keys(cardMap).sort();
        if (validIds.length === 0) return { nodes: [], nodeMap: new Map() };

        const centerX = window.innerWidth / 2;
        const centerY = (window.innerHeight - 200) / 2;
        const radius = window.innerWidth < 768 ? 140 : 350;

        const nodes = validIds.map((id, index) => {
            const angle = (index / validIds.length) * 2 * Math.PI - (Math.PI / 2);
            return { id, x: centerX + radius * Math.cos(angle), y: centerY + radius * Math.sin(angle), card: cardMap[id] };
        });
        return { nodes, nodeMap: new Map(nodes.map(n => [n.id, n])) };
    }, [cardMap]);

    const currentSnapshot = useMemo(() => {
        const totalSeconds = Math.floor(currentTime);
        const hour = Math.floor(totalSeconds / 3600);
        const minute = Math.floor((totalSeconds % 3600) / 60);
        const second = Math.floor(totalSeconds % 60);
        const targetTimeStr = `${selectedDate}T${String(hour).padStart(2,'0')}:${String(minute).padStart(2,'0')}:${String(second).padStart(2,'0')}`;
        const targetTimestamp = new Date(targetTimeStr).getTime();
        const filteredLogs = logs.filter(l => new Date(l.timestamp).getTime() <= targetTimestamp);
        const lastLog = filteredLogs[filteredLogs.length - 1];
        // ✨ 만약 마지막 로그가 코멘트라면 rootId(카드)를 활성 노드로 설정
        const activeNodeId = (lastLog?.targetType === 'COMMENT')
            ? lastLog.rootId
            : (lastLog?.targetId || null);
        const connections = new Set<string>();
        let prevCardId: string | null = null;
        filteredLogs.forEach(log => {
            // 현재 로그의 카드 ID 결정
            const currentCardId = (log.targetType === 'COMMENT') ? log.rootId : log.targetId;

            if (currentCardId && prevCardId && currentCardId !== prevCardId) {
                connections.add(`${prevCardId}|${currentCardId}`);
            }
            if (currentCardId) prevCardId = currentCardId;
        });
        return { activeNodeId, connections };
    }, [currentTime, logs, selectedDate]);

    const logMarkers = useMemo(() => {
        return logs.map(log => {
            const d = new Date(log.timestamp);
            const seconds = d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
            const percent = (seconds / SEC_PER_DAY) * 100;
            return { id: log.id, percent, seconds };
        });
    }, [logs]);

    const animate = useCallback((time: number) => {
        if (isPlaying) {
            const delta = time - lastTimeRef.current;
            const secondsToAdd = (delta / 1000) * playbackSpeed;
            setCurrentTime(prev => {
                const nextTime = prev + secondsToAdd;
                if (nextTime >= SEC_PER_DAY - 0.1) { setIsPlaying(false); return SEC_PER_DAY; }
                return nextTime;
            });
            lastTimeRef.current = time;
            requestRef.current = requestAnimationFrame(animate);
        }
    }, [isPlaying, playbackSpeed]);

    useEffect(() => {
        if (isPlaying) { lastTimeRef.current = performance.now(); requestRef.current = requestAnimationFrame(animate); }
        return () => { if (requestRef.current) cancelAnimationFrame(requestRef.current); };
    }, [isPlaying, animate]);

    const handleWheel = (e: React.WheelEvent) => {
        const scaleAmount = -e.deltaY * 0.001;
        const newScale = Math.min(Math.max(0.2, viewState.scale + scaleAmount), 5);
        const rect = e.currentTarget.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        const newX = mouseX - (mouseX - viewState.x) * (newScale / viewState.scale);
        const newY = mouseY - (mouseY - viewState.y) * (newScale / viewState.scale);
        setViewState({ scale: newScale, x: newX, y: newY });
    };

    const handleMouseDown = (e: React.MouseEvent) => { isDragging.current = true; lastMousePos.current = { x: e.clientX, y: e.clientY }; };
    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging.current) return;
        const deltaX = e.clientX - lastMousePos.current.x;
        const deltaY = e.clientY - lastMousePos.current.y;
        lastMousePos.current = { x: e.clientX, y: e.clientY };
        setViewState(prev => ({ ...prev, x: prev.x + deltaX, y: prev.y + deltaY }));
    };
    const handleMouseUp = () => { isDragging.current = false; };

    const handleTouchStart = (e: React.TouchEvent) => {
        isDragging.current = true;
        const touch = e.touches[0];
        lastMousePos.current = { x: touch.clientX, y: touch.clientY };
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging.current) return;
        const touch = e.touches[0];
        const deltaX = touch.clientX - lastMousePos.current.x;
        const deltaY = touch.clientY - lastMousePos.current.y;
        lastMousePos.current = { x: touch.clientX, y: touch.clientY };
        setViewState(prev => ({ ...prev, x: prev.x + deltaX, y: prev.y + deltaY }));
    };

    const handleTouchEnd = () => { isDragging.current = false; };

    const zoomIn = () => setViewState(prev => ({ ...prev, scale: Math.min(5, prev.scale + 0.2) }));
    const zoomOut = () => setViewState(prev => ({ ...prev, scale: Math.max(0.2, prev.scale - 0.2) }));

    const formatTime = (totalSeconds: number) => {
        const s = Math.floor(totalSeconds);
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        const sec = s % 60;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    };

    const handleGoToTimeline = () => { navigate(`/activity?time=${Math.floor(currentTime)}`); };

    const handleMarkerClick = (e: React.MouseEvent, seconds: number) => {
        e.stopPropagation();
        setIsPlaying(false);
        setCurrentTime(seconds);
        updateUrlTime(seconds);
    };

    const renderTicks = () => {
        const ticks = [];
        for (let i = 0; i <= 24; i++) {
            ticks.push(<TickMark key={i} style={{ left: `${(i / 24) * 100}%` }}><TickLabel>{i}시</TickLabel></TickMark>);
        }
        return ticks;
    };

    return (
        <PageWrapper>
            <ResponsiveHeader
                title="🎬 Replay Mode"
                leftContent={<DateInput type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} />}
                extraActions={
                    <Button onClick={handleGoToTimeline}>이 시점 Log</Button>
                }
            />

            <CanvasContainer
                onWheel={handleWheel}
                onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}
            >
                <TransformLayer style={{ transform: `translate(${viewState.x}px, ${viewState.y}px) scale(${viewState.scale})` }}>
                    <CanvasSVG>
                        <defs>
                            <marker id="flow-arrow" markerWidth="12" markerHeight="12" refX="10" refY="3" orient="auto" markerUnits="strokeWidth">
                                <path d="M0,0 L0,6 L9,3 z" fill="var(--primary-accent)" opacity="0.8" />
                            </marker>
                        </defs>
                        {Array.from(currentSnapshot.connections).map(conn => {
                            const [sourceId, targetId] = conn.split('|');
                            const source = layout.nodeMap.get(sourceId);
                            const target = layout.nodeMap.get(targetId);
                            if (!source || !target) return null;
                            const endPoint = getIntersectionPoint({ x: source.x, y: source.y }, { x: target.x, y: target.y }, CARD_WIDTH, CARD_HEIGHT);
                            return <line key={conn} x1={source.x} y1={source.y} x2={endPoint.x} y2={endPoint.y} stroke="var(--primary-accent)" strokeWidth="2" strokeOpacity="0.5" markerEnd="url(#flow-arrow)" />;
                        })}
                    </CanvasSVG>
                    {layout.nodes.map(node => (
                        <CardNodeContainer key={node.id} x={node.x} y={node.y} isActive={node.id === currentSnapshot.activeNodeId} onClick={(e) => { e.stopPropagation(); navigate(`/tasks/card/${node.id}`); }}>
                            {node.card?.labels && node.card.labels.length > 0 && (
                                <LabelContainer>{node.card.labels.map((label, i) => <LabelPill key={i} color={label.color}>{label.name}</LabelPill>)}</LabelContainer>
                            )}
                            <CardTitle>{node.card?.title || 'Unknown Card'}</CardTitle>
                            <CardMeta><span>{node.card?.cardListId ? 'In List' : ''}</span><span>ID: {node.id.slice(-4)}</span></CardMeta>
                        </CardNodeContainer>
                    ))}
                </TransformLayer>
            </CanvasContainer>

            <ZoomControls>
                <ZoomButton onClick={zoomIn}>+</ZoomButton>
                <ZoomButton onClick={zoomOut}>-</ZoomButton>
            </ZoomControls>

            <ControlBar>
                <TimeDisplay>{formatTime(currentTime)}</TimeDisplay>

                <TimelineScrollContainer>
                    <SliderWrapper widthPercent={100 * timelineZoom}>
                        <LogMarkerContainer>
                            {logMarkers.map(m => (
                                <LogMarker
                                    key={m.id}
                                    style={{ left: `${m.percent}%` }}
                                    onClick={(e) => handleMarkerClick(e, m.seconds)}
                                    title={formatTime(m.seconds)}
                                />
                            ))}
                        </LogMarkerContainer>

                        <RangeInput
                            type="range"
                            min="0"
                            max={SEC_PER_DAY}
                            step="1"
                            value={currentTime}
                            onChange={(e) => {
                                setIsPlaying(false);
                                const newTime = Number(e.target.value);
                                setCurrentTime(newTime);
                                updateUrlTime(newTime);
                            }}
                        />
                        <TickContainer>{renderTicks()}</TickContainer>
                    </SliderWrapper>
                </TimelineScrollContainer>

                <ControlsContainer>
                    <PlayButton onClick={() => setIsPlaying(!isPlaying)}>
                        {isPlaying ? <svg viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg> : <svg viewBox="0 0 24 24"><polygon points="5 3 19 12 5 21 5 3"/></svg>}
                    </PlayButton>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-dark)' }}>Speed:</span>
                        <SpeedSelect value={playbackSpeed} onChange={(e) => setPlaybackSpeed(Number(e.target.value))}>
                            {SPEED_OPTIONS.map(speed => <option key={speed} value={speed}>{speed}x</option>)}
                        </SpeedSelect>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginLeft: '1rem' }}>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-dark)' }}>Timeline:</span>
                        <TimelineZoomButton isActive={timelineZoom === 1} onClick={() => setTimelineZoom(1)}>x1</TimelineZoomButton>
                        <TimelineZoomButton isActive={timelineZoom === 2} onClick={() => setTimelineZoom(2)}>x2</TimelineZoomButton>
                        <TimelineZoomButton isActive={timelineZoom === 4} onClick={() => setTimelineZoom(4)}>x4</TimelineZoomButton>
                    </div>
                </ControlsContainer>
            </ControlBar>
        </PageWrapper>
    );
};

export default ActivityPlaybackPage;